/**
 * Account Date Range Dialog Component
 * Dialog for selecting date range when assigning accounts to communities
 */

import { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { X } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';

interface AccountDateRangeDialogProps {
  isOpen: boolean;
  accountName: string;
  onClose: () => void;
  onConfirm: (startDate: string, expirationDate: string | null) => Promise<void>;
  mode?: 'create' | 'edit';
  initialStartDate?: string;
  initialExpirationDate?: string | null;
}

export function AccountDateRangeDialog({
  isOpen,
  accountName,
  onClose,
  onConfirm,
  mode = 'create',
  initialStartDate,
  initialExpirationDate,
}: AccountDateRangeDialogProps) {
  // Helper function to convert date string to datetime-local format
  const toDateTimeLocal = (dateStr: string | undefined): string => {
    if (!dateStr) return format(new Date(), "yyyy-MM-dd'T'HH:mm");
    try {
      const date = new Date(dateStr);
      return format(date, "yyyy-MM-dd'T'HH:mm");
    } catch {
      return format(new Date(), "yyyy-MM-dd'T'HH:mm");
    }
  };

  const initialStartDateLocal = toDateTimeLocal(initialStartDate);

  const [startDate, setStartDate] = useState<string>(initialStartDateLocal);
  const [expirationDate, setExpirationDate] = useState<string>(
    initialExpirationDate ? toDateTimeLocal(initialExpirationDate) : ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string>('');

  // Validate expiration date is not before start date and dates are not in the past
  const validateDates = (start: string, expiration: string): boolean => {
    const now = new Date();
    const startDateObj = new Date(start);

    // In edit mode, allow the original start date even if it's in the past.
    const isStartDateChanged = mode === 'edit' && start !== initialStartDateLocal;
    if ((mode !== 'edit' || isStartDateChanged) && startDateObj < now) {
      setDateError('Start date cannot be in the past');
      return false;
    }

    if (!expiration) {
      setDateError('');
      return true; // Expiration is optional
    }

    const expirationDateObj = new Date(expiration);

    // Check if expiration date is in the past
    if (expirationDateObj < now) {
      setDateError('Expiration date cannot be in the past');
      return false;
    }

    // Check if expiration is before start
    if (expirationDateObj < startDateObj) {
      setDateError('Expiration date cannot be before start date');
      return false;
    }

    setDateError('');
    return true;
  };

  const handleStartDateChange = (newStartDate: string) => {
    setStartDate(newStartDate);
    // Always validate when start date changes
    validateDates(newStartDate, expirationDate);
  };

  const handleExpirationDateChange = (newExpirationDate: string) => {
    setExpirationDate(newExpirationDate);
    validateDates(startDate, newExpirationDate);
  };

  const handleConfirm = async () => {
    // Final validation before submit
    if (expirationDate && !validateDates(startDate, expirationDate)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(startDate, expirationDate || null);
      onClose();
    } catch (error) {
      // Error handled by store (toast shown)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Account Assignment' : 'Assign Account to Community'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? `Update the date range for ${accountName}'s membership in this community.`
              : `Set the date range for ${accountName}'s membership in this community.`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="startDate" className="text-base font-semibold">Start Date & Time *</Label>
            <Input
              id="startDate"
              type="datetime-local"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              min={mode !== 'edit' ? format(new Date(), "yyyy-MM-dd'T'HH:mm") : undefined}
              required
              className="text-base h-11"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="expirationDate" className="text-base font-semibold">Expiration Date & Time (Optional)</Label>
            <div className="relative">
              <Input
                id="expirationDate"
                type="datetime-local"
                value={expirationDate}
                onChange={(e) => handleExpirationDateChange(e.target.value)}
                min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                className="text-base h-11 pr-16"
              />
              {expirationDate && (
                <button
                  type="button"
                  onClick={() => { setExpirationDate(''); setDateError(''); }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear expiration date"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {dateError && (
              <p className="text-sm text-red-500">{dateError}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting || !startDate || !!dateError}>
            {isSubmitting
              ? mode === 'edit'
                ? 'Updating...'
                : 'Assigning...'
              : mode === 'edit'
                ? 'Update Assignment'
                : 'Assign Account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
